import { useEffect, useMemo, useState } from "react";

import { TV_CONNECT_PLAYBACK_TICK_MS } from "$src/core/constants/tv-connect";

import useTvConnectRemote from "./use-tv-connect-remote";

const extrapolateProgress = ({
  currentTime,
  duration,
  now,
  status,
  ts,
}: {
  currentTime: number;
  duration?: number;
  now: number;
  status: "playing" | "paused" | "idle";
  ts?: number;
}) => {
  if (status !== "playing") return currentTime;

  const elapsedSeconds = ts != null ? (now - ts) / 1000 : 0;
  const progress = currentTime + elapsedSeconds;

  return duration != null ? Math.min(progress, duration) : progress;
};

const useTvConnectRemotePlaybackState = () => {
  const { playback } = useTvConnectRemote();

  const [now, setNow] = useState(() => Date.now());

  /* Re-sync immediately when the host publishes a new position. */
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setNow(Date.now());
  }, [playback?.currentTime, playback?.ts]);

  /* Advance the local clock each second while playing. */
  useEffect(() => {
    if (playback?.status !== "playing") return undefined;

    const interval = setInterval(() => {
      setNow(Date.now());
    }, TV_CONNECT_PLAYBACK_TICK_MS);

    return () => clearInterval(interval);
  }, [playback?.status]);

  const progress = useMemo(() => {
    const currentTime = playback?.currentTime ?? 0;
    const status = playback?.status ?? "idle";

    return extrapolateProgress({
      currentTime,
      duration: playback?.duration,
      now,
      status,
      ts: playback?.ts,
    });
  }, [
    now,
    playback?.currentTime,
    playback?.duration,
    playback?.status,
    playback?.ts,
  ]);

  const hasPlayback =
    playback?.status !== "idle" &&
    (!!playback?.asset?.title || !!playback?.asset?.contentId);

  const isAdInProgress = playback?.ad === true;

  const isLive =
    hasPlayback &&
    !isAdInProgress &&
    (playback.live === true ||
      playback.duration == null ||
      playback.duration === Infinity);

  return useMemo(() => {
    if (isAdInProgress) {
      return {
        ...playback,
        type: "ad" as const,
        progress,
        duration: playback.duration ?? 0,
      };
    }

    if (isLive) {
      return {
        ...playback,
        type: "live" as const,
        progress: null,
        duration: null,
      };
    }

    if (hasPlayback) {
      return {
        ...playback,
        type: "playback" as const,
        progress,
        duration: playback.duration ?? 0,
      };
    }

    return {
      type: "idle" as const,
      status: "idle" as const,
      asset: null,
      progress: null,
      duration: null,
      muted: null,
      trailer: null,
    };
  }, [hasPlayback, isAdInProgress, isLive, playback, progress]);
};

export default useTvConnectRemotePlaybackState;
